import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Building2, 
  User, 
  Phone, 
  Mail, 
  Globe, 
  MapPin, 
  Save, 
  Calendar,
  Hash,
  Edit,
  X,
  Upload,
  Image,
  Link2
} from "lucide-react";
import UnsavedChangesGuard from "@/components/UnsavedChangesGuard";

// Countries list
const PRIORITY_COUNTRIES = ["México", "Estados Unidos", "Canadá", "China", "Argentina"];
const OTHER_COUNTRIES = [
  "Afganistán", "Albania", "Alemania", "Andorra", "Angola", "Antigua y Barbuda", "Arabia Saudita",
  "Argelia", "Armenia", "Australia", "Austria", "Azerbaiyán", "Bahamas", "Bangladés", "Barbados",
  "Baréin", "Bélgica", "Belice", "Benín", "Bielorrusia", "Birmania", "Bolivia", "Bosnia y Herzegovina",
  "Botsuana", "Brasil", "Brunéi", "Bulgaria", "Burkina Faso", "Burundi", "Bután", "Cabo Verde",
  "Camboya", "Camerún", "Chad", "Chile", "Chipre", "Ciudad del Vaticano", "Colombia", "Comoras",
  "Corea del Norte", "Corea del Sur", "Costa de Marfil", "Costa Rica", "Croacia", "Cuba", "Dinamarca",
  "Dominica", "Ecuador", "Egipto", "El Salvador", "Emiratos Árabes Unidos", "Eritrea", "Eslovaquia",
  "Eslovenia", "España", "Estonia", "Etiopía", "Filipinas", "Finlandia", "Fiyi", "Francia", "Gabón",
  "Gambia", "Georgia", "Ghana", "Granada", "Grecia", "Guatemala", "Guinea", "Guinea Ecuatorial",
  "Guinea-Bisáu", "Guyana", "Haití", "Honduras", "Hungría", "India", "Indonesia", "Irak", "Irán",
  "Irlanda", "Islandia", "Islas Marshall", "Islas Salomón", "Israel", "Italia", "Jamaica", "Japón",
  "Jordania", "Kazajistán", "Kenia", "Kirguistán", "Kiribati", "Kuwait", "Laos", "Lesoto", "Letonia",
  "Líbano", "Liberia", "Libia", "Liechtenstein", "Lituania", "Luxemburgo", "Macedonia del Norte",
  "Madagascar", "Malasia", "Malaui", "Maldivas", "Malí", "Malta", "Marruecos", "Mauricio", "Mauritania",
  "Micronesia", "Moldavia", "Mónaco", "Mongolia", "Montenegro", "Mozambique", "Namibia", "Nauru",
  "Nepal", "Nicaragua", "Níger", "Nigeria", "Noruega", "Nueva Zelanda", "Omán", "Países Bajos",
  "Pakistán", "Palaos", "Palestina", "Panamá", "Papúa Nueva Guinea", "Paraguay", "Perú", "Polonia",
  "Portugal", "Qatar", "Reino Unido", "República Centroafricana", "República Checa",
  "República del Congo", "República Democrática del Congo", "República Dominicana", "Ruanda",
  "Rumania", "Rusia", "Samoa", "San Cristóbal y Nieves", "San Marino", "San Vicente y las Granadinas",
  "Santa Lucía", "Santo Tomé y Príncipe", "Senegal", "Serbia", "Seychelles", "Sierra Leona",
  "Singapur", "Siria", "Somalia", "Sri Lanka", "Suazilandia", "Sudáfrica", "Sudán", "Sudán del Sur",
  "Suecia", "Suiza", "Surinam", "Tailandia", "Tanzania", "Tayikistán", "Timor Oriental", "Togo",
  "Tonga", "Trinidad y Tobago", "Túnez", "Turkmenistán", "Turquía", "Tuvalu", "Ucrania", "Uganda",
  "Uruguay", "Uzbekistán", "Vanuatu", "Venezuela", "Vietnam", "Yemen", "Yibuti", "Zambia", "Zimbabue"
].filter(c => !PRIORITY_COUNTRIES.includes(c)).sort();

const ALL_COUNTRIES = [...PRIORITY_COUNTRIES, "---", ...OTHER_COUNTRIES];

const SOURCE_OPTIONS = [
  "Recomendación Cliente",
  "Recomendación Interna",
  "Página WEB",
  "Redes Sociales",
  "Publicidad Digital",
  "Publicidad Impresa",
  "Radio-TV"
];

const ClientDetail = () => {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchClient();
  }, [id]);

  // Detectar cambios cuando está en modo edición
  useEffect(() => {
    if (editing && client) {
      const hasChanges = JSON.stringify(editData) !== JSON.stringify(client);
      setHasUnsavedChanges(hasChanges);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [editData, client, editing]);

  const fetchClient = async () => {
    try {
      const res = await axios.get(`${API}/clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClient(res.data);
      setEditData(res.data);
    } catch (error) {
      toast.error("Error al cargar cliente");
      navigate("/clientes/crm");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cliente actualizado");
      setClient(editData);
      setEditing(false);
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setEditData(client);
    setEditing(false);
    setHasUnsavedChanges(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Solo se permiten archivos de imagen");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo es muy grande. Máximo 5MB.");
      return;
    }

    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await axios.post(`${API}/upload/logo`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setEditData(prev => ({ ...prev, logo_url: res.data.url }));
      toast.success("Logo subido correctamente");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Error al subir el logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setEditData(prev => ({ ...prev, logo_url: "" }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Cargando cliente...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-8 text-center">
        <p>Cliente no encontrado</p>
        <Button onClick={() => navigate("/clientes/crm")}>Volver al CRM</Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="client-detail-page">
      <UnsavedChangesGuard 
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={handleSave}
        onDiscard={handleDiscardChanges}
      />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/clientes/crm")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{client.company_name}</h1>
              <p className="text-slate-500 text-sm font-mono">{client.folio}</p>
            </div>
          </div>
          {isAdmin && !editing && (
            <Button onClick={() => setEditing(true)} variant="outline">
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          )}
          {editing && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleDiscardChanges}>
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          )}
        </div>

        {/* Registration Info */}
        <Card className="mb-6 bg-slate-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Registrado: {formatDate(client.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Por: {client.created_by_name || "—"}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Client Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Company Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="w-5 h-5" />
                Información de la Empresa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Folio</Label>
                <div className="flex items-center gap-2 p-2 bg-slate-100 rounded font-mono">
                  <Hash className="w-4 h-4 text-slate-400" />
                  {client.folio}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Empresa / Razón Social</Label>
                {editing ? (
                  <Input
                    value={editData.company_name}
                    onChange={(e) => setEditData({...editData, company_name: e.target.value.toUpperCase()})}
                    style={{ textTransform: 'uppercase' }}
                  />
                ) : (
                  <p className="p-2 bg-slate-50 rounded font-medium">{client.company_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Nombre comercial del cliente</Label>
                {editing ? (
                  <>
                    <Input
                      value={editData.nombre_comercial || ""}
                      onChange={(e) => setEditData({...editData, nombre_comercial: e.target.value})}
                      placeholder="Cómo se mostrará en documentos al cliente final"
                      data-testid="nombre-comercial-input"
                    />
                    <p className="text-xs text-slate-400">Si se deja vacío, se usa la razón social.</p>
                  </>
                ) : (
                  <p className="p-2 bg-slate-50 rounded font-medium">{client.nombre_comercial || client.company_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>RFC</Label>
                {editing ? (
                  <Input
                    value={editData.rfc}
                    onChange={(e) => setEditData({...editData, rfc: e.target.value.toUpperCase()})}
                  />
                ) : (
                  <p className="p-2 bg-slate-50 rounded font-mono">{client.rfc}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>País de Origen</Label>
                {editing ? (
                  <Select value={editData.country || ""} onValueChange={(v) => setEditData({...editData, country: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar país" />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_COUNTRIES.map((country, idx) => (
                        country === "---" ? (
                          <SelectItem key={idx} value="---" disabled>──────────</SelectItem>
                        ) : (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        )
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="p-2 bg-slate-50 rounded flex items-center gap-2">
                    <Globe className="w-4 h-4 text-slate-400" />
                    {client.country || "—"}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Dirección</Label>
                {editing ? (
                  <Input
                    value={editData.address || ""}
                    onChange={(e) => setEditData({...editData, address: e.target.value})}
                  />
                ) : (
                  <p className="p-2 bg-slate-50 rounded flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {client.address || "—"}
                  </p>
                )}
              </div>
              
              {/* Logo del Cliente */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Logo del Cliente
                </Label>
                {editing ? (
                  <div>
                    {editData.logo_url ? (
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <img 
                            src={editData.logo_url.startsWith('http') ? editData.logo_url : `${API.replace('/api', '')}${editData.logo_url}`}
                            alt="Logo del cliente" 
                            className="h-16 w-auto max-w-[150px] object-contain border rounded-lg p-2 bg-white"
                          />
                          <button
                            type="button"
                            onClick={removeLogo}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="text-sm text-green-600">Logo cargado</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                          <div className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                            <Upload className="w-4 h-4 text-slate-500" />
                            <span className="text-sm text-slate-600">
                              {uploadingLogo ? "Subiendo..." : "Subir logo"}
                            </span>
                          </div>
                        </label>
                        <span className="text-xs text-slate-400">PNG, JPG hasta 5MB</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-2 bg-slate-50 rounded">
                    {client.logo_url ? (
                      <img 
                        src={client.logo_url.startsWith('http') ? client.logo_url : `${API.replace('/api', '')}${client.logo_url}`}
                        alt="Logo del cliente" 
                        className="h-16 w-auto max-w-[150px] object-contain"
                      />
                    ) : (
                      <span className="text-slate-400 flex items-center gap-2">
                        <Image className="w-4 h-4" /> Sin logo
                      </span>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5" />
                Información de Contacto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del Contacto</Label>
                {editing ? (
                  <Input
                    value={editData.contact_name}
                    onChange={(e) => setEditData({...editData, contact_name: e.target.value})}
                  />
                ) : (
                  <p className="p-2 bg-slate-50 rounded font-medium">{client.contact_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                {editing ? (
                  <Input
                    value={editData.phone}
                    onChange={(e) => setEditData({...editData, phone: e.target.value})}
                  />
                ) : (
                  <p className="p-2 bg-slate-50 rounded flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    {client.phone}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                {editing ? (
                  <Input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData({...editData, email: e.target.value})}
                  />
                ) : (
                  <p className="p-2 bg-slate-50 rounded flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {client.email}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>¿Cómo nos conoció?</Label>
                {editing ? (
                  <Select value={editData.source || ""} onValueChange={(v) => setEditData({...editData, source: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar opción" />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_OPTIONS.map(option => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="p-2 bg-slate-50 rounded">{client.source || "—"}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Vinculación Bind ERP */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Link2 className="w-5 h-5" />
                Vinculación Bind ERP
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>No. Cliente en Bind</Label>
                  {editing ? (
                    <Input
                      value={editData.bind_client_id || ""}
                      onChange={(e) => setEditData({...editData, bind_client_id: e.target.value})}
                      placeholder="Ej: CLI-00123"
                      data-testid="bind-client-id-input"
                    />
                  ) : (
                    <p className="p-2 bg-slate-50 rounded font-mono">{client.bind_client_id || "—"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Días de crédito</Label>
                  {editing ? (
                    <Input
                      type="number"
                      min={0}
                      value={editData.dias_credito ?? 0}
                      onChange={(e) => setEditData({...editData, dias_credito: e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0})}
                      placeholder="0"
                      data-testid="dias-credito-input"
                    />
                  ) : (
                    <p className="p-2 bg-slate-50 rounded font-mono">{client.dias_credito ?? 0} días</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Estatus ERP</Label>
                  <div className="p-2">
                    {(editing ? editData.bind_client_id : client.bind_client_id) ? (
                      <Badge className="bg-green-100 text-green-700 border border-green-300" data-testid="bind-status-badge">Vinculado</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-slate-100 text-slate-500" data-testid="bind-status-badge">Desvinculado</Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {!isAdmin && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            Solo los administradores pueden editar la información del cliente.
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDetail;
